<template>
  <v-container class="pa-0">
    <v-row>
      <v-col cols="12" md="4" offset-md="4">
        <ValidationObserver ref="observer">
          <h2>Enter a new password</h2>

          <v-form @submit.prevent="submit">
            <p
              class="text-left mt-6"
              v-if="whitelabel.authentication.signUp.showEmail"
            >
              Email: <b>{{ verifyUserLinkCode.email }}</b>
            </p>
            <p
              class="text-left mt-6"
              v-if="whitelabel.authentication.signUp.showUniqueIdentifier"
            >
              Industry Number: <b>{{ verifyUserLinkCode.uniqueIdentifier }}</b>
            </p>
            <p
              class="text-left mt-6"
              v-if="whitelabel.authentication.signUp.showPhone"
            >
              Phone: <b>{{ verifyUserLinkCode.phone }}</b>
            </p>
            <ValidationProvider
              id="password"
              v-slot="{ errors }"
              name="password"
              rules="required|password"
            >
              <v-text-field
                v-model="password"
                :error-messages="errors"
                :append-icon="showPassword ? icons.mdiEye : icons.mdiEyeOff"
                :type="showPassword ? 'text' : 'password'"
                label="Password"
                @click:append="showPassword = !showPassword"
              />
            </ValidationProvider>

            <ValidationProvider
              id="confirm_password"
              v-slot="{ errors }"
              name="confirm_password"
              rules="required|confirmed:password"
            >
              <v-text-field
                v-model="confirmPassword"
                :error-messages="errors"
                :append-icon="
                  showConfirmPassword ? icons.mdiEye : icons.mdiEyeOff
                "
                :type="showConfirmPassword ? 'text' : 'password'"
                label="Confirm Password"
                @click:append="showConfirmPassword = !showConfirmPassword"
              />
            </ValidationProvider>

            <ValidationProvider
              v-if="newUser"
              id="accepted_terms"
              v-slot="{ errors }"
              name="accepted_terms"
              :rules="{ is: [true] }"
            >
              <v-checkbox
                v-model="acceptedTerms"
                color="primary"
                :error-messages="errors"
              >
                <template #label>
                  <div>
                    By signing up, I accept
                    <a
                      target="_blank"
                      :href="whitelabel.termsAndConditionsLocation"
                      @click.stop
                      >{{ whitelabel.name }}'s terms &amp; conditions</a
                    >
                  </div>
                </template>
              </v-checkbox>
            </ValidationProvider>

            <v-btn
              rounded
              large
              depressed
              type="submit"
              color="primary"
              class="mt-12"
              :loading="buttonLoading"
            >
              <span>Save</span><v-icon>{{ icons.mdiArrowRight }}</v-icon>
            </v-btn>
          </v-form>
        </ValidationObserver>
      </v-col>
    </v-row>

    <ErrorSnackbar v-model="showError" />
  </v-container>
</template>

<script>
  import ErrorSnackbar from '../../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../../mixins/form-helper-mixin';
  import { RESET_PASSWORD_MUTATION } from '../../../constants/graphql/users';
  import loginService from '../../../services/LoginService';

  export default {
    name: 'ResetPassword',
    components: {
      ErrorSnackbar,
    },
    mixins: [formHelperMixin],
    props: {
      verifyUserLinkCode: {
        required: true,
        type: Object,
      },
    },
    data(props) {
      return {
        loading: true,
        password: null,
        confirmPassword: null,
        acceptedTerms: false,
        showPassword: false,
        showConfirmPassword: false,
        email: props.verifyUserLinkCode.email,
        phone: props.verifyUserLinkCode.phone,
        unique_identifier: !!props.verifyUserLinkCode.uniqueIdentifier
          ? props.verifyUserLinkCode.uniqueIdentifier
          : props.verifyUserLinkCode.email,
      };
    },
    computed: {
      newUser() {
        return !this.verifyUserLinkCode.acceptedTerms;
      },
    },
    methods: {
      submit() {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.buttonLoading = true;
            this.resetPassword();
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      resetPassword() {
        const resetPasswordCode = this.$route.params.id;
        const password = this.password;
        const acceptedTerms = this.acceptedTerms;

        this.$apollo
          .mutate({
            mutation: RESET_PASSWORD_MUTATION,
            variables: {
              resetPasswordCode,
              password,
              acceptedTerms,
            },
          })
          .then(result => {
            this.login(this.unique_identifier, password);
          })
          .catch(error => {
            this.handleMutationError(error);
          });
      },
      login(unique_identifier, password) {
        loginService
          .login(unique_identifier, password, this.$apollo, this.$store)
          .then(result => {
            const login = result.data.login;
            const accessToken = login.accessToken;
            const refreshToken = login.refreshToken;
            const testatorId = login.testatorId;
            const userId = login.userId;
            const willCountry = login.willCountry;

            this.$store.dispatch('login', {
              accessToken,
              refreshToken,
              testatorId,
              userId,
              willCountry,
            });
            loginService.setSentryUser(userId);
            loginService.redirectToNext(this.$router);
          })
          .catch(error => {
            this.buttonLoading = false;
            if (error.message.includes('GraphQL error:')) {
              this.errorMessage = error.message
                .replace('GraphQL error:', '')
                .trim();
            } else {
              this.errorMessage =
                "Something went wrong, we're looking into it. Please try login again.";
              throw error;
            }
          });
      },
      handleMutationError(error) {
        this.buttonLoading = false;
        this.displayError();
        throw error;
      },
    },
  };
</script>
